import { takeLatest, put, select, call, all } from 'redux-saga/effects'
import { getLanguageData, getLanguages, getBaseTranslations } from '../../utils/apiCalls'
import { decryptCredentials, setItem } from '../../utils/storageUtils'
import { getDefaultLanguage, stripTrailingSlash } from '../../utils/routes'
import { filterLanguagesByConfig, mergeLanguageData } from '../../utils/language'
import {
  getChatProviderSuccess,
  getLanguageDataFailure,
  getLanguageDataStart,
  getLanguageDataSuccess,
  getLanguagesFailure,
  getLanguagesStart,
  getLanguagesSuccess,
  getLicenseDetailSuccess,
  setBrandLogo,
  setSelectedLanguagesComplete,
  setSelectedLanguagesStart,
  setUserCountry,
  setPhoneVerificationType
} from '../redux-slices/language'
import { showBanner, showChildren, showHeader } from '../redux-slices/loader'
import { setMerchantId } from '../redux-slices/payment'
import { setIsUserBlock } from '../redux-slices/user'

export default function * languageWatcher () {
  yield takeLatest(getLanguagesStart.type, getLanguagesWorker)
  yield takeLatest(setSelectedLanguagesStart.type, setSelectedLanguageWorker)
  yield takeLatest(getLanguageDataStart, getLanguageDataWorker)
}

const getLang = state => state.language.languages

function * getLanguagesWorker (action) {
  try {
    const { isOverrideLanguage, allowedLanguageByConfig } = action?.payload || {}

    const { data: response } = yield getLanguages()

    let allowedLanguages = response?.data?.languages?.allowedLanguages
    if (isOverrideLanguage) {
      allowedLanguages = filterLanguagesByConfig(allowedLanguages, allowedLanguageByConfig)
    }

    yield put(getLanguagesSuccess({ allowedLanguages }))
    yield put(setMerchantId(response?.data?.merchantId))
    yield put(setUserCountry(response?.data?.countryCode || 'SE'))
    yield put(setPhoneVerificationType(response?.data?.phoneVerification?.registrationType ?? null))

    if (response?.data?.countryBlocked) {
      yield put(setIsUserBlock(true))
    }

    const siteId = response?.data?.siteId
    if (process.env.REACT_APP_CIO_NEW_SOURCE === 'true') {
      const writeKey = response?.data?.writeKey
      const region = response?.data?.region
      if (writeKey) {
        window.cioEnableScript(writeKey, region, siteId)
      } else {
        console.error('Customer.io: new JS source is enabled but no writeKey was returned — CIO is disabled for this application')
      }
    } else if (siteId) {
      window.cioEnableScript(siteId)
    }
  } catch (e) {
    yield put(getLanguagesFailure())
  }
}

function * setSelectedLanguageWorker (action) {
  try {
    const { selectedLanguageCode, navigate } = action && action.payload
    const languages = yield select(getLang)
    const requestedLanguage = selectedLanguageCode?.toUpperCase()
    // Fall back to the site's own default, not a hardcoded 'EN' that a mirror may not allow
    const myLanguage = languages?.allowedLanguages?.includes(requestedLanguage)
      ? requestedLanguage
      : getDefaultLanguage(languages)
    const languageSegment = myLanguage.toLowerCase()

    yield setItem('language', myLanguage)

    let newPath = ''
    if (window?.location?.pathname?.split('/')?.length > 2) {
      // Replace the language code in the URL path with the language that was actually applied
      newPath = window?.location?.pathname?.replace(`/${window?.location?.pathname?.split('/')?.[1]?.toLowerCase()}/`, `/${languageSegment}/`)
    } else {
      // Create a new path with just the language code if the URL has less than 2 segments
      newPath = `/${languageSegment}`
    }

    if (navigate) yield navigate(stripTrailingSlash(newPath))
    yield put(setSelectedLanguagesComplete({
      selectedLanguageCode: myLanguage
    }))

    yield put(getLanguageDataStart())
  } catch (e) {
    yield put(setSelectedLanguagesComplete())
  }
}

function * fetchBaseTranslationsSafe () {
  try {
    return yield call(getBaseTranslations)
  } catch (err) {
    console.error('Error fetching base translations - ', err)
    return { data: {} }
  }
}

function * getLanguageDataWorker (action) {
  try {
    const data = action && action.payload

    const [baseRes, res] = yield all([
      call(fetchBaseTranslationsSafe),
      call(getLanguageData, { manual: !data })
    ])

    const baseTranslations = (baseRes?.data && typeof baseRes.data === 'object' && !Array.isArray(baseRes.data))
      ? baseRes.data
      : {}
    const apiLanguageData = (res?.data?.data?.languageData && typeof res?.data?.data?.languageData === 'object' && !Array.isArray(res?.data?.data?.languageData))
      ? res.data.data.languageData
      : {}

    const mergedLanguageData = mergeLanguageData(baseTranslations, apiLanguageData)

    yield put(getLanguageDataSuccess(mergedLanguageData))

    const chatProvider = res?.data?.data?.chatSupportSystemDetails
    const decryptedData = {}

    try {
      if (chatProvider) {
        Object.keys(chatProvider).forEach((key) => {
          decryptedData[key] = JSON.parse(decryptCredentials(chatProvider[key]))
        })
      }
      yield put(getChatProviderSuccess(decryptedData))
    } catch (err) {
      console.log('Error in parsing', err)
      // silently handle the error in parsing
    }

    yield put(getLicenseDetailSuccess(res?.data?.data?.licenseTags))
    yield put(setBrandLogo(res?.data?.data?.logoUrl))

    if (res?.data?.countryBlocked) {
      yield put(setIsUserBlock(true))
    }

    yield put(showHeader(true))

    yield put(showChildren(true))

    yield put(showBanner(true))
  } catch (e) {
    // const { navigate } = action?.payload
    // if (e?.response?.data?.errors[0]?.errorCode === 3000) {
    //   navigate && navigate(`/${getItem('language').toLowerCase() || 'en'}/600`)
    // }
    console.error('Error in get language - ', e)
    yield put(getLanguageDataFailure())
    yield put(setIsUserBlock(true))
  }
}
